import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import { OptionType } from '@app/core/interfaces/enterprise-option-definition';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { UserLevel } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { License } from '@app/shared/models/license';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { hasEnterpriseLicense } from '@app/utils/has-enterprise-license';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, skipUntil, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseAuthorizationService {
  constructor(
    private authenticationService: AuthenticationService,
    private endUserContextService: EndUserContextService,
    private enterpriseContextService: EnterpriseContextService,
    private menuService: MenuService
  ) {}

  getEnterpriseOption$(optionType: EnterpriseOptionType): Observable<EnterpriseOption | undefined> {
    return this.enterpriseContextService.enterpriseOptions.data$.pipe(
      map((enterpriseOptions) => enterpriseOptions.find((option) => option.name === optionType))
    );
  }

  hasAnyEnterpriseOption$(optionNames: EnterpriseOptionType[]): Observable<boolean> {
    return this.getEnterpriseOptions$(optionNames).pipe(
      map((options) => {
        const anyOptionIsActive: boolean = options.some((option) => option?.active === true);

        return anyOptionIsActive;
      })
    );
  }

  hasEnterpriseLicense$(license: License): Observable<boolean> {
    if (this.authenticationService.userMinimalLevel(UserLevel.EnterpriseAdmin)) {
      return this.enterpriseContextService.enterprise.data$.pipe(
        take(1),
        map((enterprise) => hasEnterpriseLicense(enterprise.c5, license))
      );
    } else {
      return this.endUserContextService.domain$.pipe(
        take(1),
        map((domain) => hasEnterpriseLicense(domain, license))
      );
    }
  }

  hasEnterpriseOption$(necessaryEnterpriseOption: EnterpriseOptionType): Observable<boolean> {
    return this.hasEnterpriseOptions$([necessaryEnterpriseOption]);
  }

  hasEnterpriseOptions$(optionNames: EnterpriseOptionType[]): Observable<boolean> {
    return this.getEnterpriseOptions$(optionNames).pipe(
      map((options) => {
        const hasAllOptions: boolean = options.length === optionNames.length;
        const allOptionsAreActive: boolean = options.every((option) => {
          const isActive = option.active === true;
          const isAvailable = option.available == null || option.available > 0;

          return isActive && isAvailable;
        });

        return hasAllOptions && allOptionsAreActive;
      })
    );
  }

  /**
   * Looks for the `enterpriseOption` and checks its `available` against `used` values.
   * When the option is not found on the enterprise, or is inactive, then `false` or the value in `withDefault` is returned.
   * @param enterpriseOptionType Enterprise option to look for.
   * @param withDefault optional return value if the enterprise-option was not found
   * @returns the result of the found enterprise-option `used < available`, otherwise `false` or the specified `withDefault` value.
   */
  licenseIsAvailable$(
    enterpriseOptionType: EnterpriseOptionType,
    withDefault?: boolean
  ): Observable<boolean> {
    return this.getEnterpriseOption$(enterpriseOptionType).pipe(
      map((enterpriseOption) => {
        if (enterpriseOption != null && enterpriseOption.active === true) {
          return (enterpriseOption.used || 0) < enterpriseOption.available;
        }

        return withDefault ?? false;
      })
    );
  }

  licensesLeft$(param: EnterpriseOptionType): Observable<number> {
    return this.getEnterpriseOption$(param).pipe(
      map((option) => {
        let available = 0;

        if (option != null) {
          const licenses = option.available - option.used;

          if (licenses > 0) {
            available = licenses;
          }
        }

        return available;
      })
    );
  }

  /**
   * Checks current state of enterprise-options if the option is found on enterprise and if it currently active.
   * @param optionType name of the enterprise option
   * @returns an observable that emits one value and completes.
   */
  hasAnyActiveEnterpriseOption$(...optionType: EnterpriseOptionType[]): Observable<boolean> {
    const optionTypesSet = new Set(optionType);
    return this.enterpriseContextService.enterpriseOptions.data$.pipe(
      take(1),
      // map((os) => os.some((o) => o.name === optionType && o.active === true))
      map((os) =>
        os.some((o) => {
          const isThereAndActive = optionTypesSet.has(o.name) && o.active === true;
          const checkCounterOption = o.definition?.optionType === OptionType.COUNT ? o.count > 0 : true;
          return isThereAndActive && checkCounterOption;
        })
      )
    );
  }

  private getEnterpriseOptions$(optionNames: EnterpriseOptionType[]): Observable<EnterpriseOption[]> {
    return this.menuService.menuData$.pipe(
      mergeMap((menuData) => {
        if (menuData.activeUserLevels.isEnterprise || menuData.activeUserLevels.isEndUser) {
          return this.enterpriseContextService.enterpriseOptions.data$.pipe(
            skipUntil(
              this.enterpriseContextService.enterpriseOptions.loading$.pipe(
                filter((loading) => loading === false),
                take(1)
              )
            ),
            map((options) => options.filter((option) => optionNames.includes(option.name)))
          );
        } else {
          return of([]);
        }
      })
    );
  }
}
